import { Link } from '@customink/pigment-react';
import {
  type Product,
  type ProjectItem,
  useComplementsAllowed,
  usePricing,
} from '@customink/project-service-react-components';
import type { ReactEventHandler } from 'react';

import { ColorWheelIcon } from '../ColorWheelIcon';
import { DelimitedSet } from '../DelimitedSet';

import { useInternalPreview } from '@/hooks/internalPreview';
import { useProjectItemsByDesign } from '@/hooks/Project/useProjectItemsByDesign';
import { spacing4 } from '@/utils/designTokens';

type ProjectItemButtonsProps = {
  onEditClick: ReactEventHandler;
  onAddClick: ReactEventHandler;
  onRemoveClick: ReactEventHandler;
  product?: Product;
  item: ProjectItem;
};

const linkStyle = {
  fontSize: '14px',
  lineHeight: '22px',
  whiteSpace: 'nowrap',
};

export const ProjectItemButtons = ({
  onEditClick,
  onAddClick,
  onRemoveClick,
  product,
  item,
}: ProjectItemButtonsProps) => {
  const showAddColor = useComplementsAllowed(item);
  const { isLoading: isLoadingPricing } = usePricing();
  const { isMultiStyle: showRemove } = useProjectItemsByDesign(
    item.design?.cid ?? '',
  );
  const isInternalPreview = useInternalPreview();

  // Don't render any buttons in internal preview mode
  if (isInternalPreview) {
    return null;
  }

  return (
    <DelimitedSet
      elements={[
        <Link
          button
          data-testid="edit-sizes-link"
          disabled={isLoadingPricing}
          key="edit"
          onClick={onEditClick}
          sx={linkStyle}
        >
          Edit {product?.style_type === 'apparel' ? 'Sizes' : 'Quantity'}
        </Link>,

        showAddColor && (
          <Link
            button
            data-testid="add-another-color-link"
            disabled={isLoadingPricing}
            key="add"
            onClick={onAddClick}
            sx={{
              ...linkStyle,
              alignItems: 'center',
              display: 'flex',
              gap: spacing4,
            }}
          >
            <ColorWheelIcon />
            <span>Add Another Color</span>
          </Link>
        ),

        showRemove && (
          <Link
            button
            data-testid="remove-item-link"
            disabled={isLoadingPricing}
            key="remove"
            onClick={onRemoveClick}
            sx={linkStyle}
          >
            Remove
          </Link>
        ),
      ]}
      isLoading={isLoadingPricing}
    />
  );
};
